import { useEffect, useMemo, useState } from "react";
import { fetchApi } from "../../redux/api";

// The summaries endpoint caps the list at 300 approval numbers per call.
const CHUNK_SIZE = 250;

/**
 * Audit status for a page of releases.
 *
 * The release lists come from stored procedures whose column lists predate the
 * audit module, so this keeps one lookup per page and lets every row render the
 * same badge the audit screens use.
 *
 * Returns a map: `{ [approval_no]: { audit_status, exception_count } }`.
 */
export const useReleaseAudit = (approvalNos = []) => {
  const [summaries, setSummaries] = useState({});

  // Depend on the contents, not the array identity: list pages rebuild their
  // rows on every render and would otherwise refetch forever.
  const key = useMemo(
    () =>
      Array.from(new Set((approvalNos || []).filter(Boolean)))
        .sort()
        .join("|"),
    [approvalNos]
  );

  useEffect(() => {
    const nos = key ? key.split("|") : [];
    if (!nos.length) {
      setSummaries({});
      return undefined;
    }

    let cancelled = false;
    const chunks = [];
    for (let i = 0; i < nos.length; i += CHUNK_SIZE) {
      chunks.push(nos.slice(i, i + CHUNK_SIZE));
    }

    Promise.all(
      chunks.map((chunk) =>
        fetchApi(
          `audit/release-summaries?approval_nos=${encodeURIComponent(chunk.join(","))}`
        ).catch(() => null)
      )
    ).then((responses) => {
      if (cancelled) return;
      const merged = {};
      responses.forEach((response) => {
        if (response?.success && response.data) {
          Object.assign(merged, response.data);
        }
      });
      setSummaries(merged);
    });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return summaries;
};

/**
 * Exceptions + audit status for one release, used by the badge/banner on the
 * release and PV detail screens.
 */
export const useReleaseAuditDetail = (approvalNo) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!approvalNo) {
      setDetail(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchApi(`audit/release/${encodeURIComponent(approvalNo)}`)
      .then((response) => {
        if (cancelled) return;
        setDetail(response?.success ? response : null);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [approvalNo]);

  return { detail, loading };
};

export default useReleaseAudit;
